import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Shared chrome for all public legal / info pages.
 *  - Golden cursive "taraanaya" splash header
 *  - Back button
 *  - Title + last-updated subtitle
 *  - Company-identity card (legal name, CIN, regd office)
 *  - Sections rendered as numbered cards
 *  - Universal footer (CIN, contact, legal nav)
 */

const StarArc = ({ size = 36 }) => (
  <svg width={size} height={size * 0.275} viewBox="0 0 200 55" style={{ display: 'block' }}>
    <defs>
      <linearGradient id="lpsArc" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#F3E5AB" stopOpacity="0.9" />
        <stop offset="50%" stopColor="#D4AF37" stopOpacity="0.95" />
        <stop offset="100%" stopColor="#B8962E" stopOpacity="0.9" />
      </linearGradient>
      <linearGradient id="lpsStar" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F7E6A8" />
        <stop offset="55%" stopColor="#D4AF37" />
        <stop offset="100%" stopColor="#9E7820" />
      </linearGradient>
    </defs>
    <path d="M 25 45 Q 60 14,100 8 T 175 45" stroke="url(#lpsArc)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    {[{x:25,y:45,s:6},{x:62,y:18,s:8},{x:100,y:8,s:10},{x:138,y:18,s:8},{x:175,y:45,s:6}].map((st,i)=>(
      <path key={i} fill="url(#lpsStar)"
        d={`M ${st.x} ${st.y-st.s} L ${st.x+st.s*.35} ${st.y-st.s*.35} L ${st.x+st.s} ${st.y} L ${st.x+st.s*.35} ${st.y+st.s*.35} L ${st.x} ${st.y+st.s} L ${st.x-st.s*.35} ${st.y+st.s*.35} L ${st.x-st.s} ${st.y} L ${st.x-st.s*.35} ${st.y-st.s*.35} Z`}/>
    ))}
  </svg>
);

const goldGradient = 'linear-gradient(90deg,#F3E5AB 0%,#D4AF37 50%,#B8962E 100%)';

const FOOTER_LINKS = [
  { href: '/about', label: 'About' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/privacy-policy', label: 'Privacy' },
  { href: '/terms-of-service', label: 'Terms' },
  { href: '/refund-policy', label: 'Refund' },
  { href: '/contact', label: 'Contact' },
];

export default function LegalPageShell({ title, subtitle, lastUpdated, sections, dataTestId }) {
  const navigate = useNavigate();
  const fontStack = "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif";

  return (
    <div data-testid={dataTestId} style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at top, #1a2332 0%, #0a0e17 60%)',
      color: 'rgba(255,255,255,0.85)',
    }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 20, paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))', paddingBottom: 60 }}>

        {/* Brand link to home */}
        <a href="/watch" data-testid="brand-link" style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2, textDecoration: 'none', marginBottom: 22 }}>
          <StarArc size={42} />
          <span style={{
            fontFamily: "'Dancing Script', cursive", fontSize: 26, fontWeight: 600, lineHeight: 1,
            backgroundImage: goldGradient, WebkitBackgroundClip: 'text', backgroundClip: 'text',
            WebkitTextFillColor: 'transparent', color: 'transparent', letterSpacing: '0.02em',
            marginTop: -4,
          }}>taraanaya</span>
        </a>

        {/* Back */}
        <button data-testid="legal-back-btn" onClick={() => navigate(-1)} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 22, padding: '8px 16px',
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
          color: 'rgba(255,255,255,0.7)', fontSize: 14, cursor: 'pointer', borderRadius: 20, fontFamily: fontStack,
        }}>
          <ArrowLeft size={16} /> Back
        </button>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 'clamp(26px, 5vw, 34px)', color: '#fff', fontWeight: 700, margin: 0, fontFamily: fontStack, letterSpacing: '-0.3px' }}>{title}</h1>
          {subtitle && <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', marginTop: 6, fontFamily: fontStack }}>{subtitle}</p>}
          {lastUpdated && <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginTop: 6, fontFamily: fontStack }}>Last updated: {lastUpdated}</p>}
        </div>

        {/* Company Identity Block */}
        <div style={{
          background: 'rgba(116, 179, 255, 0.05)', border: '1px solid rgba(116, 179, 255, 0.18)',
          borderRadius: 12, padding: '14px 18px', marginBottom: 22, fontSize: 13.5, lineHeight: 1.65,
          color: 'rgba(255,255,255,0.72)', fontFamily: fontStack,
        }}>
          <strong style={{ color: '#fff' }}>Taraanaya Vedtech Private Limited</strong>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12.5 }}>
            CIN: U96906UW2026PTC249734 &nbsp;|&nbsp; Central Registration Centre (RoC, MCA)<br />
            Regd. Office: C-3/101, 1st Floor, Tower C3, Cleo County, Sector 121, Noida, Gautam Buddha Nagar, UP – 201301, India.
          </div>
        </div>

        {/* Sections */}
        {sections && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {sections.map((s) => (
              <section key={s.num} data-testid={`legal-section-${s.num}`} style={{
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 12, padding: '18px 22px',
              }}>
                <h2 style={{
                  fontSize: 'clamp(15px, 3.8vw, 17px)', color: '#fff', fontWeight: 600, margin: '0 0 10px 0', fontFamily: fontStack,
                }} dangerouslySetInnerHTML={{ __html: `${s.num}. ${s.title}` }} />
                <div className="lps-content" style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(255,255,255,0.72)', fontFamily: fontStack }}>
                  <style>{`
                    .lps-content ul { padding-left: 20px; margin-top: 6px; }
                    .lps-content li { margin-bottom: 6px; }
                    .lps-content li strong { color: rgba(255,255,255,0.92); }
                    .lps-content p { margin: 0 0 8px 0; }
                    .lps-content p:last-child { margin-bottom: 0; }
                    .lps-content table { border-collapse: collapse; width: 100%; margin: 8px 0; }
                    .lps-content th, .lps-content td { border: 1px solid rgba(255,255,255,0.1); padding: 8px 10px; font-size: 14px; text-align: left; }
                    .lps-content th { background: rgba(255,255,255,0.04); color: #fff; font-weight: 600; }
                    .lps-content a { color: #74B3FF; text-decoration: none; border-bottom: 1px solid rgba(116,179,255,0.3); }
                  `}</style>
                  {s.content}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Universal footer */}
        <footer style={{
          marginTop: 48, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.08)',
          textAlign: 'center', fontFamily: fontStack,
        }}>
          <nav style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '16px 22px', marginBottom: 18 }}>
            {FOOTER_LINKS.map((l) => (
              <a key={l.href} href={l.href} data-testid={`footer-link-${l.label.toLowerCase()}`} style={{
                fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase',
                color: 'rgba(243,229,171,0.7)', textDecoration: 'none', borderBottom: '1px solid transparent',
              }}>{l.label}</a>
            ))}
          </nav>

          <div style={{ marginBottom: 14, fontSize: 12, color: 'rgba(148,163,184,0.7)' }}>
            <a href="mailto:taraanaya@taraanaya.ai" style={{ color: 'rgba(243,229,171,0.85)', textDecoration: 'none' }}>taraanaya@taraanaya.ai</a>
            &nbsp;&nbsp;|&nbsp;&nbsp; Mon&ndash;Sat &middot; 10:00 AM &ndash; 7:00 PM IST
          </div>

          <p style={{ fontSize: 11, color: 'rgba(148,163,184,0.5)', lineHeight: 1.7, margin: 0 }}>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>Taraanaya Vedtech Private Limited</span><br />
            CIN: U96906UW2026PTC249734 &nbsp;|&nbsp; Regd. Office: C-3/101, Cleo County, Sector 121, Noida, UP &ndash; 201301, India.<br />
            <span style={{ marginTop: 6, display: 'inline-block', color: 'rgba(148,163,184,0.45)' }}>
              &copy; 2026 Taraanaya Vedtech Private Limited. All rights reserved.
            </span>
          </p>
        </footer>
      </div>
    </div>
  );
}
